/**
 * PastDueBanner
 * Global banner shown above the header when billing status is past_due.
 * Only visible to project owners. Redirects to billing portal on "Pay now".
 *
 * @see BILLING_IMPLEMENTATION_GUIDE.md
 */

import React, { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  useBillingRedirect,
  usePastDueBannerVisible,
} from "../../../hooks/useBilling";
import { useUserStore } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";
import { Button } from "../UIElements/Button";
import { Icon } from "../UIElements/Icon";

/** Approximate banner height for layout padding (px) */
export const PAST_DUE_BANNER_HEIGHT = 80;

const PastDueBanner = ({ onHeightChange } = {}) => {
  const bannerRef = useRef(null);
  const { t } = useTranslation(["billing"]);
  const toast = useToastContext();
  const user = useUserStore((state) => state.user);
  const showBanner = usePastDueBannerVisible();
  const { redirectToBillingPortal, isRedirecting } = useBillingRedirect();

  useLayoutEffect(() => {
    if (!showBanner) {
      onHeightChange?.(0);
      return;
    }
    const banner = bannerRef.current;
    if (!banner) return;
    const measure = () =>
      onHeightChange?.(Math.ceil(banner.getBoundingClientRect().height));
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(banner);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      onHeightChange?.(0);
    };
  }, [showBanner, onHeightChange]);

  if (!showBanner) {
    return null;
  }

  const userName = user?.name || user?.email || "there";

  const handlePayNow = () => {
    redirectToBillingPortal().catch((err) =>
      toast.error(err?.message || t("redirectFailed")),
    );
  };

  return (
    <div
      ref={bannerRef}
      className="bg-destructive text-destructive-foreground fixed inset-x-0 top-0 flex items-center justify-between gap-4 px-4 py-3 shadow-lg sm:px-6"
      style={{ zIndex: "var(--layer-header, 400)" }}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Icon
          name="AlertTriangle"
          className="text-destructive-foreground h-6 w-6 flex-shrink-0"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {t("pastDueTitle", { user: userName })}
          </p>
          <p className="mt-0.5 text-sm opacity-95">{t("pastDueSubtitle")}</p>
        </div>
      </div>
      <Button
        type="button"
        onClick={handlePayNow}
        disabled={isRedirecting}
        emphasis="solid"
        intent="neutral"
        size="touch"
        className="min-w-0 flex-shrink-0 px-4 py-2 whitespace-normal"
      >
        {isRedirecting ? (
          <span className="flex items-center gap-2">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {t("paying")}
          </span>
        ) : (
          t("payNow")
        )}
      </Button>
    </div>
  );
};

export default PastDueBanner;
